import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Calendar,
  Users,
  DollarSign,
  FileText,
  Play,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Clock,
  TrendingUp,
  Eye,
  XCircle,
  CreditCard,
  RotateCcw,
  Trash2,
  UserPlus,
  EyeOff,
  Search,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { SearchableSelect } from '@/components/ui/searchable-select';

const fmtCurrency = (v: number | string) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(v));

const fmtDate = (d: string) => {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

type Scenario = 'early' | 'normal' | 'late' | 'paid';
const SCENARIO_LABEL: Record<Scenario, string> = {
  early: 'Pronto pago',
  normal: 'Normal',
  late: 'Con recargo',
  paid: 'Pagada',
};
const SCENARIO_COLOR: Record<Scenario, string> = {
  early: 'bg-green-100 text-green-800',
  normal: 'bg-yellow-100 text-yellow-800',
  late: 'bg-red-100 text-red-800',
  paid: 'bg-gray-100 text-gray-600',
};

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  CASH: 'Efectivo', TRANSFER: 'Transferencia', CARD: 'Tarjeta', CHECK: 'Cheque', CREDIT: 'Crédito', OTHER: 'Otro',
};
const fmtPaymentMethods = (v: string | null) =>
  v ? v.split(',').map(m => PAYMENT_METHOD_LABEL[m] ?? m).join(' + ') : '—';

function getScenario(dueDate: string, graceDeadline: string, status: string): Scenario {
  if (['paid_early', 'paid_normal', 'paid_late'].includes(status)) return 'paid';
  const [y1, m1, d1] = dueDate.split('-').map(Number);
  const [y2, m2, d2] = graceDeadline.split('-').map(Number);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(y1, m1 - 1, d1);
  const grace = new Date(y2, m2 - 1, d2);
  if (today <= due) return 'early';
  if (today <= grace) return 'normal';
  return 'late';
}

interface Group {
  id: number;
  name: string;
  code: string;
  status: string;
  startDate?: string | null;
  endDate?: string | null;
  defaultTuitionProduct: { name: string; price: string; taxRate: string } | null;
}

/**
 * Rounds a tuition base amount using the "5 se queda" rule:
 *   - Hundredths 0–5 → tenths stays (round down)
 *   - Hundredths 6–9 → tenths rounds up
 * Then: tenths 0–4 → floor | tenths 5 → keep .5 | tenths 6–9 → ceil
 *
 * Examples: 5.54 → 5.5 | 5.56 → 6 | 5.55 → 5.5 | 4.46 → 4.5
 */
function roundTuitionCustom(value: number): number {
  const cents = Math.round(value * 100);
  const intPart = Math.floor(cents / 100);
  const centsPart = cents % 100;
  const tenths = Math.floor(centsPart / 10);
  const hundredths = centsPart % 10;
  const adjTenths = tenths + (hundredths >= 6 ? 1 : 0);
  if (adjTenths >= 10) return intPart + 1;
  if (adjTenths <= 4) return intPart;
  if (adjTenths === 5) return intPart + 0.5;
  return intPart + 1;
}

/**
 * Suggests the first due date for a tuition plan based on the group's start date.
 * Returns the 5th of the month following the start date.
 * Falls back to the 5th of next month from today when no start date is provided.
 */
function suggestFirstDueDate(startDate?: string | null): string {
  let baseYear: number;
  let baseMonth: number; // 1-indexed

  if (startDate) {
    const [y, m] = startDate.split('-').map(Number);
    baseYear = y;
    baseMonth = m;
  } else {
    const today = new Date();
    baseYear = today.getFullYear();
    baseMonth = today.getMonth() + 1;
  }

  // Move to the next month
  let nextMonth = baseMonth + 1;
  let nextYear = baseYear;
  if (nextMonth > 12) { nextMonth = 1; nextYear++; }

  return `${nextYear}-${String(nextMonth).padStart(2, '0')}-05`;
}

interface TuitionPlan {
  id: number;
  name: string;
  groupId: number;
  groupName: string;
  groupCode: string;
  totalAmount: string;
  installmentCount: number;
  discountPct: string;
  graceDays: number;
  surchargePct: string;
  firstDueDate: string;
  intervalDays: number;
  status: 'active' | 'voided';
  notes: string | null;
  createdAt: string;
  stats: { total: number; pending: number; paid: number };
}

interface Installment {
  id: number;
  planId: number;
  planName?: string;
  studentId: number;
  studentNui: string | null;
  studentName: string;
  installmentNumber: number;
  baseAmount: string;
  discountAmount: string;
  surchargeAmount: string;
  dueDate: string;
  graceDeadline: string;
  status: string;
  paidAt: string | null;
  paidAmount: string | null;
  paymentMethods: string | null;
  scenario: Scenario;
  effectiveAmount: number;
  invoiceTotal: number | null;
  discountAmountWithTax: number | null;
  baseAmountWithTax: number | null;
  surchargeAmountWithTax: number | null;
}

type ActiveTab = 'create' | 'plans' | 'installments';

export default function TuitionGeneration() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ActiveTab>('create');
  const [selectedPlan, setSelectedPlan] = useState<TuitionPlan | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [showVoided, setShowVoided] = useState(false);
  const [planSearch, setPlanSearch] = useState('');
  const [excludedWarning, setExcludedWarning] = useState<
    { studentId: number; studentName: string; estatus: string }[] | null
  >(null);

  const [firstDueDateAutoSet, setFirstDueDateAutoSet] = useState(false);

  const [form, setForm] = useState({
    groupId: '',
    name: '',
    totalAmount: '',
    installmentCount: 14,
    discountPct: 10,
    graceDays: 5,
    surchargePct: 10,
    firstDueDate: suggestFirstDueDate(),
    intervalDays: 30,
    notes: '',
  });

  const { data: groups = [] } = useQuery<Group[]>({
    queryKey: ['academic-periods-all'],
    queryFn: async () => {
      const r = await fetch('/api/academic-periods', { credentials: 'include' });
      if (!r.ok) throw new Error('Error al cargar grupos');
      const all = await r.json();
      return all.filter((g: Group) => ['active', 'promocion', 'draft'].includes(g.status));
    },
  });

  const { data: plans = [], isLoading: loadingPlans } = useQuery<TuitionPlan[]>({
    queryKey: ['tuition-plans'],
    queryFn: async () => {
      const r = await fetch('/api/sales/tuitions/plans', { credentials: 'include' });
      if (!r.ok) throw new Error('Error al cargar planes');
      return r.json();
    },
    enabled: activeTab === 'plans' || activeTab === 'installments',
  });

  const { data: installments = [], isLoading: loadingInstallments } = useQuery<Installment[]>({
    queryKey: ['tuition-installments', selectedPlan?.id, selectedStudentId],
    queryFn: async () => {
      if (!selectedPlan) return [];
      const params = selectedStudentId ? `?studentId=${selectedStudentId}` : '';
      const r = await fetch(`/api/sales/tuitions/plans/${selectedPlan.id}/installments${params}`, { credentials: 'include' });
      if (!r.ok) throw new Error('Error al cargar cuotas');
      return r.json();
    },
    enabled: !!selectedPlan && activeTab === 'installments',
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/sales/tuitions/plans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          groupId: Number(form.groupId),
          name: form.name,
          totalAmount: Number(form.totalAmount),
          installmentCount: form.installmentCount,
          discountPct: form.discountPct,
          graceDays: form.graceDays,
          surchargePct: form.surchargePct,
          firstDueDate: form.firstDueDate,
          intervalDays: form.intervalDays,
          notes: form.notes || undefined,
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error); }
      return r.json();
    },
    onSuccess: (res) => {
      let desc = '';
      if (res.totalStudents === 0) {
        desc = 'Plan creado. No hay alumnos inscritos en este grupo aún.';
      } else if (res.totalInvoices > 0) {
        desc = `${res.totalInstallments} cuotas y ${res.totalInvoices} cuentas de cobro generadas para ${res.totalStudents} alumnos.`;
      } else {
        desc = `${res.totalInstallments} cuotas generadas para ${res.totalStudents} alumnos. (Sin recibos: el grupo no tiene producto vinculado.)`;
      }
      setExcludedWarning(res.excludedStudents?.length ? res.excludedStudents : null);
      toast({ title: 'Plan creado', description: desc });
      queryClient.invalidateQueries({ queryKey: ['tuition-plans'] });
      setForm(f => ({ ...f, name: '', totalAmount: '', notes: '' }));
      setActiveTab('plans');
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const voidMutation = useMutation({
    mutationFn: async (planId: number) => {
      const r = await fetch(`/api/sales/tuitions/plans/${planId}/void`, { method: 'POST', credentials: 'include' });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: 'Plan anulado' });
      queryClient.invalidateQueries({ queryKey: ['tuition-plans'] });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (planId: number) => {
      const r = await fetch(`/api/sales/tuitions/plans/${planId}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: 'Plan eliminado' });
      queryClient.invalidateQueries({ queryKey: ['tuition-plans'] });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const addStudentsMutation = useMutation({
    mutationFn: async (planId: number) => {
      const r = await fetch(`/api/sales/tuitions/plans/${planId}/add-missing-students`, { method: 'POST', credentials: 'include' });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error); }
      return r.json();
    },
    onSuccess: (res) => {
      setExcludedWarning(res.excludedStudents?.length ? res.excludedStudents : null);
      if (res.added === 0) {
        toast({ title: 'Sin cambios', description: 'Todos los alumnos activos del grupo ya tienen cuotas generadas.' });
      } else {
        toast({
          title: `${res.added} alumno(s) agregado(s)`,
          description: res.totalInvoices > 0
            ? `Cuotas y ${res.totalInvoices} cuentas de cobro generadas.`
            : `Cuotas generadas. El grupo no tiene producto vinculado (sin recibos).`,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['tuition-plans'] });
      queryClient.invalidateQueries({ queryKey: ['tuition-installments'] });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const selectedGroup = useMemo(
    () => groups.find(g => g.id === Number(form.groupId)) ?? null,
    [groups, form.groupId]
  );

  const previewTaxRate = useMemo(() => {
    const raw = selectedGroup?.defaultTuitionProduct?.taxRate ?? '0';
    return parseFloat(raw) / 100; // e.g. 16 → 0.16
  }, [selectedGroup]);

  const previewInstallments = useMemo(() => {
    if (!form.totalAmount || !form.installmentCount || !form.firstDueDate) return [];
    const total = Number(form.totalAmount);
    const count = form.installmentCount;
    const base = roundTuitionCustom(total / count);
    const lastBase = roundTuitionCustom(total - base * (count - 1));
    const r2 = (v: number) => Math.round(v * 100) / 100;

    const toDateStr = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const result = [];
    for (let i = 0; i < count; i++) {
      const b = i === count - 1 ? lastBase : base;
      const discAmt  = r2(b * (1 - form.discountPct / 100));
      const surAmt   = r2(b * (1 + form.surchargePct / 100));
      const discWithIVA = r2(discAmt * (1 + previewTaxRate));
      const baseWithIVA = r2(b      * (1 + previewTaxRate));
      const surWithIVA  = r2(surAmt * (1 + previewTaxRate));
      const [y, m, d] = form.firstDueDate.split('-').map(Number);
      // Pin to same day-of-month each month; clamp to last day if month is shorter
      const lastDayOfTargetMonth = new Date(y, m + i, 0).getDate();
      const clampedDay = Math.min(d, lastDayOfTargetMonth);
      const due = new Date(y, m - 1 + i, clampedDay);
      const dueStr = toDateStr(due);
      const grace = new Date(due);
      grace.setDate(grace.getDate() + (form.graceDays || 0));
      const graceStr = toDateStr(grace);
      const surchargeStart = new Date(grace);
      surchargeStart.setDate(surchargeStart.getDate() + 1);
      const surchargeStartStr = toDateStr(surchargeStart);
      result.push({ num: i + 1, base: b, discAmt, surAmt, discWithIVA, baseWithIVA, surWithIVA, dueStr, graceStr, surchargeStartStr });
    }
    return result;
  }, [form.totalAmount, form.installmentCount, form.firstDueDate, form.intervalDays, form.discountPct, form.surchargePct, form.graceDays, previewTaxRate]);

  const uniqueStudents = useMemo(() => {
    const map = new Map<number, string>();
    installments.forEach(i => map.set(i.studentId, i.studentName));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [installments]);

  const filteredInstallments = useMemo(() => {
    if (!selectedStudentId) return installments;
    return installments.filter(i => i.studentId === Number(selectedStudentId));
  }, [installments, selectedStudentId]);

  const groupOptions = groups
    .map(g => ({ value: g.id.toString(), label: `${g.code} - ${g.name}` }));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/app/sales/invoices">
          <Button variant="ghost" size="sm">
            <ChevronLeft className="h-4 w-4 mr-1" /> Volver
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-6 w-6 text-blue-600" />
            Generación de Colegiaturas
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Planes de pago con descuento por pronto pago, periodo de gracia y recargos por mora
          </p>
        </div>
      </div>

      <div className="flex border-b">
        {([
          { id: 'create', label: 'Crear Plan', icon: Play },
          { id: 'plans', label: 'Planes Activos', icon: FileText },
          { id: 'installments', label: 'Ver Cuotas', icon: CreditCard },
        ] as const).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {excludedWarning && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <div className="flex items-start justify-between gap-4">
              <div>
                <strong>{excludedWarning.length} alumno(s) del grupo no se incluyeron</strong> por su estatus actual — revísalos antes de continuar:
                <ul className="list-disc list-inside mt-1">
                  {excludedWarning.map(s => (
                    <li key={s.studentId}>{s.studentName} — {s.estatus}</li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => setExcludedWarning(null)}
                className="text-current opacity-70 hover:opacity-100 shrink-0"
                aria-label="Cerrar aviso"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {activeTab === 'create' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Configuración del Plan</CardTitle>
              <CardDescription>El sistema generará cuotas para todos los alumnos activos del grupo</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <Label>Grupo *</Label>
                <SearchableSelect
                  options={groupOptions}
                  value={form.groupId || null}
                  onChange={v => {
                    const grp = groups.find(g => g.id === Number(v));
                    const price = grp?.defaultTuitionProduct?.price
                      ? String(Number(grp.defaultTuitionProduct.price))
                      : '';
                    const planName = grp ? `Plan de Colegiaturas - ${grp.name}` : '';
                    const suggestedDue = suggestFirstDueDate(grp?.startDate);
                    setFirstDueDateAutoSet(!!grp?.startDate);
                    setForm(f => ({
                      ...f,
                      groupId: v ? String(v) : '',
                      totalAmount: price || f.totalAmount,
                      name: planName || f.name,
                      firstDueDate: suggestedDue,
                    }));
                  }}
                  placeholder="Seleccionar grupo..."
                />
              </div>

              <div className="space-y-1">
                <Label>Nombre del Plan *</Label>
                <Input
                  placeholder="Ej: Colegiatura 2025-2026 Generación A"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>

              <div className="space-y-1">
                <Label>Costo Total de la Carrera *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder="80000.00"
                    className="pl-7"
                    value={form.totalAmount}
                    onChange={e => setForm(f => ({ ...f, totalAmount: e.target.value }))}
                  />
                </div>
                {form.groupId && selectedGroup?.defaultTuitionProduct && (
                  <p className="text-xs text-green-600">
                    Precio cargado automáticamente del catálogo de productos. Puedes ajustarlo si es necesario.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Número de Cuotas *</Label>
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    value={form.installmentCount}
                    onChange={e => setForm(f => ({ ...f, installmentCount: parseInt(e.target.value) || 14 }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Intervalo (días)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.intervalDays}
                    onChange={e => setForm(f => ({ ...f, intervalDays: parseInt(e.target.value) || 30 }))}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label>Fecha de 1er Vencimiento *</Label>
                <Input
                  type="date"
                  value={form.firstDueDate}
                  onChange={e => {
                    setFirstDueDateAutoSet(false);
                    setForm(f => ({ ...f, firstDueDate: e.target.value }));
                  }}
                />
                {firstDueDateAutoSet ? (
                  <p className="text-xs text-blue-600">
                    ✦ Calculado automáticamente: día 5 del mes siguiente al inicio del grupo. Puedes ajustarlo.
                  </p>
                ) : (
                  <p className="text-xs text-gray-400">Inamovible. No se recorre por festivos ni domingos.</p>
                )}
              </div>

              <div className="border rounded-lg p-3 bg-blue-50 space-y-3">
                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Reglas de Cobro</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-green-700">% Descuento Pronto Pago</Label>
                    <Input
                      type="number"
                      min={0}
                      max={50}
                      value={form.discountPct}
                      onChange={e => setForm(f => ({ ...f, discountPct: parseFloat(e.target.value) || 0 }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-yellow-700">Días Gracia</Label>
                    <Input
                      type="number"
                      min={0}
                      max={30}
                      value={form.graceDays}
                      onChange={e => setForm(f => ({ ...f, graceDays: parseInt(e.target.value) || 0 }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-red-700">% Recargo Mora</Label>
                    <Input
                      type="number"
                      min={0}
                      max={50}
                      value={form.surchargePct}
                      onChange={e => setForm(f => ({ ...f, surchargePct: parseFloat(e.target.value) || 0 }))}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="text-xs text-blue-600 space-y-1">
                  <p>✓ <strong>Escenario A</strong> — Pago ≤ vencimiento: precio con {form.discountPct}% descuento</p>
                  <p>⏳ <strong>Escenario B</strong> — {form.graceDays} días después: precio normal sin penalización</p>
                  <p>⚠ <strong>Escenario C</strong> — Día {form.graceDays + 1}+: precio + {form.surchargePct}% recargo</p>
                </div>
                <div className="border-t border-blue-200 pt-2">
                  <p className="text-xs text-blue-700 font-medium">
                    ✦ Redondeo de cuotas: &nbsp;
                    <span className="font-normal">0–4 baja al entero · <strong>5 se queda en .5</strong> · 6–9 sube al entero</span>
                  </p>
                  <p className="text-xs text-blue-500 mt-0.5">
                    Ej: 5.54 → 5.50 &nbsp;|&nbsp; 5.55 → 5.50 &nbsp;|&nbsp; 5.56 → 6.00 &nbsp;|&nbsp; 4.46 → 4.50
                  </p>
                </div>
              </div>

              <div className="space-y-1">
                <Label>Notas (opcional)</Label>
                <Input
                  placeholder="Observaciones del plan..."
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>

              <Button
                className="w-full"
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending || !form.groupId || !form.name || !form.totalAmount || !form.firstDueDate}
              >
                {createMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generando cuotas...</>
                ) : (
                  <><Play className="h-4 w-4 mr-2" />Crear Plan y Generar Cuotas</>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle className="text-lg">Vista Previa de Cuotas</CardTitle>
              <CardDescription>
                {previewInstallments.length > 0 ? (
                  <span>
                    {previewInstallments.length} mensualidades · Base: {fmtCurrency(form.totalAmount || 0)}
                    {previewTaxRate > 0 && (
                      <span className="ml-2 text-blue-600 font-medium">
                        · c/IVA ({(previewTaxRate * 100).toFixed(0)}%): {fmtCurrency(previewInstallments.reduce((a, i) => a + i.baseWithIVA, 0))}
                      </span>
                    )}
                  </span>
                ) : 'Completa el formulario para ver la vista previa'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {previewInstallments.length > 0 ? (
                <div className="overflow-auto max-h-[520px] border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Vencimiento</th>
                        <th className="px-3 py-2 text-right font-medium text-green-700">
                          <div>Pronto pago</div>
                          <div className="text-xs font-normal text-green-600 normal-case">
                            {previewTaxRate > 0 ? 'c/IVA · hasta vencimiento' : 'hasta vencimiento'}
                          </div>
                        </th>
                        <th className="px-3 py-2 text-right font-medium text-yellow-700">
                          <div>Normal</div>
                          <div className="text-xs font-normal text-yellow-600 normal-case">
                            {previewTaxRate > 0 ? 'c/IVA · hasta días de gracia' : 'hasta días de gracia'}
                          </div>
                        </th>
                        <th className="px-3 py-2 text-right font-medium text-red-700">
                          <div>Con recargo</div>
                          <div className="text-xs font-normal text-red-500 normal-case">
                            {previewTaxRate > 0 ? 'c/IVA · a partir del vencimiento' : 'a partir del vencimiento'}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewInstallments.map(inst => (
                        <tr key={inst.num} className="border-t hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-500 font-mono">{inst.num}</td>
                          <td className="px-3 py-2 font-medium">{fmtDate(inst.dueStr)}</td>
                          <td className="px-3 py-2 text-right">
                            {previewTaxRate > 0 ? (
                              <>
                                <div className="text-green-700 font-medium">{fmtCurrency(inst.discWithIVA)}</div>
                                <div className="text-xs text-gray-400">base {fmtCurrency(inst.discAmt)}</div>
                              </>
                            ) : (
                              <div className="text-green-700 font-medium">{fmtCurrency(inst.discAmt)}</div>
                            )}
                            <div className="text-xs text-green-500">hasta {fmtDate(inst.dueStr)}</div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {previewTaxRate > 0 ? (
                              <>
                                <div className="text-yellow-700 font-medium">{fmtCurrency(inst.baseWithIVA)}</div>
                                <div className="text-xs text-gray-400">base {fmtCurrency(inst.base)}</div>
                              </>
                            ) : (
                              <div className="text-yellow-700">{fmtCurrency(inst.base)}</div>
                            )}
                            {form.graceDays > 0 ? (
                              <div className="text-xs text-yellow-500">hasta {fmtDate(inst.graceStr)}</div>
                            ) : (
                              <div className="text-xs text-gray-400">sin días de gracia</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {previewTaxRate > 0 ? (
                              <>
                                <div className="text-red-700 font-medium">{fmtCurrency(inst.surWithIVA)}</div>
                                <div className="text-xs text-gray-400">base {fmtCurrency(inst.surAmt)}</div>
                              </>
                            ) : (
                              <div className="text-red-700">{fmtCurrency(inst.surAmt)}</div>
                            )}
                            <div className="text-xs text-red-400">desde {fmtDate(inst.surchargeStartStr)}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2">
                      <tr>
                        <td colSpan={2} className="px-3 py-2 font-semibold text-gray-600">
                          Total{previewTaxRate > 0 && <span className="font-normal text-xs text-blue-600 ml-1">c/IVA</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-green-700">
                          {fmtCurrency(previewInstallments.reduce((a, i) => a + (previewTaxRate > 0 ? i.discWithIVA : i.discAmt), 0))}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-yellow-700">
                          {fmtCurrency(previewInstallments.reduce((a, i) => a + (previewTaxRate > 0 ? i.baseWithIVA : i.base), 0))}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-red-700">
                          {fmtCurrency(previewInstallments.reduce((a, i) => a + (previewTaxRate > 0 ? i.surWithIVA : i.surAmt), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Calendar className="h-16 w-16 mb-4 opacity-30" />
                  <p className="text-sm">Ingresa el monto total, número de cuotas y primera fecha de vencimiento</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'plans' && (
        <div className="space-y-4">
          {/* Filter bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {/* Search input */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={planSearch}
                onChange={e => setPlanSearch(e.target.value)}
                placeholder="Buscar por nombre, grupo o clave…"
                className="w-full pl-9 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent bg-white"
              />
              {planSearch && (
                <button
                  onClick={() => setPlanSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-3 ml-auto">
              <p className="text-sm text-gray-500">
                {plans.filter(p => {
                  if (!showVoided && p.status === 'voided') return false;
                  if (planSearch) {
                    const q = planSearch.toLowerCase();
                    return p.name.toLowerCase().includes(q) || (p.groupCode ?? '').toLowerCase().includes(q) || (p.groupName ?? '').toLowerCase().includes(q);
                  }
                  return true;
                }).length} plan(es)
                {!showVoided && plans.some(p => p.status === 'voided') && (
                  <span className="ml-1 text-gray-400">
                    ({plans.filter(p => p.status === 'voided').length} anulado(s) oculto(s))
                  </span>
                )}
              </p>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowVoided(v => !v)}
                className="text-gray-500 gap-1"
              >
                {showVoided ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                {showVoided ? 'Ocultar anulados' : 'Mostrar anulados'}
              </Button>
            </div>
          </div>

          {loadingPlans ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>
          ) : plans.filter(p => {
              if (!showVoided && p.status === 'voided') return false;
              if (planSearch) {
                const q = planSearch.toLowerCase();
                return p.name.toLowerCase().includes(q) || (p.groupCode ?? '').toLowerCase().includes(q) || (p.groupName ?? '').toLowerCase().includes(q);
              }
              return true;
            }).length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center py-16 text-gray-400">
                <Search className="h-12 w-12 mb-4 opacity-30" />
                <p className="font-medium">{planSearch ? 'Sin resultados para tu búsqueda' : 'No hay planes activos'}</p>
                <p className="text-sm mt-1">
                  {planSearch
                    ? <button onClick={() => setPlanSearch('')} className="text-indigo-500 hover:underline">Limpiar búsqueda</button>
                    : 'Crea un plan desde la pestaña "Crear Plan"'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {plans.filter(p => {
                if (!showVoided && p.status === 'voided') return false;
                if (planSearch) {
                  const q = planSearch.toLowerCase();
                  return p.name.toLowerCase().includes(q) || (p.groupCode ?? '').toLowerCase().includes(q) || (p.groupName ?? '').toLowerCase().includes(q);
                }
                return true;
              }).map(plan => (
                <Card key={plan.id} className={plan.status === 'voided' ? 'opacity-60 border-dashed' : ''}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-base truncate">{plan.name}</span>
                          {plan.status === 'voided' && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Anulado</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mb-3">
                          {plan.groupCode} — {plan.groupName}
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="bg-gray-50 rounded p-2">
                            <p className="text-xs text-gray-500">Total carrera</p>
                            <p className="font-semibold text-sm">{fmtCurrency(plan.totalAmount)}</p>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <p className="text-xs text-gray-500">Cuotas</p>
                            <p className="font-semibold text-sm">{plan.installmentCount} × {fmtCurrency(Number(plan.totalAmount) / plan.installmentCount)}</p>
                          </div>
                          <div className="bg-green-50 rounded p-2">
                            <p className="text-xs text-green-600">Pronto pago / Recargo</p>
                            <p className="font-semibold text-sm text-green-700">-{plan.discountPct}% / +{plan.surchargePct}%</p>
                          </div>
                          <div className="bg-blue-50 rounded p-2">
                            <p className="text-xs text-blue-600">1er vencimiento</p>
                            <p className="font-semibold text-sm text-blue-700">{fmtDate(plan.firstDueDate)}</p>
                          </div>
                        </div>
                        <div className="flex gap-4 mt-3 text-xs text-gray-500">
                          <span><strong className="text-gray-700">{plan.stats.total}</strong> cuotas totales</span>
                          <span><strong className="text-green-700">{plan.stats.paid}</strong> pagadas</span>
                          <span><strong className="text-yellow-700">{plan.stats.pending}</strong> pendientes</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedPlan(plan); setSelectedStudentId(''); setActiveTab('installments'); }}
                        >
                          <Eye className="h-4 w-4 mr-1" /> Ver cuotas
                        </Button>
                        {plan.status === 'active' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              disabled={addStudentsMutation.isPending}
                              onClick={() => addStudentsMutation.mutate(plan.id)}
                            >
                              {addStudentsMutation.isPending
                                ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                : <UserPlus className="h-4 w-4 mr-1" />}
                              Agregar alumnos
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => {
                                if (confirm(`¿Anular el plan "${plan.name}"? Se cancelarán todas las cuotas pendientes.`)) {
                                  voidMutation.mutate(plan.id);
                                }
                              }}
                            >
                              <XCircle className="h-4 w-4 mr-1" /> Anular
                            </Button>
                          </>
                        )}
                        {plan.status === 'voided' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-700 border-red-300 hover:bg-red-50"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              if (confirm(`¿Eliminar permanentemente el plan "${plan.name}"? Esta acción no se puede deshacer.`)) {
                                deleteMutation.mutate(plan.id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-1" /> Eliminar
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'installments' && (
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <div className="flex-1">
                  <Label className="text-xs text-gray-500 mb-1 block">Plan</Label>
                  <SearchableSelect
                    options={plans.map(p => ({ value: p.id.toString(), label: `${p.name} — ${p.groupCode}` }))}
                    value={selectedPlan?.id.toString() || null}
                    onChange={v => {
                      const p = plans.find(x => x.id === Number(v));
                      setSelectedPlan(p || null);
                      setSelectedStudentId('');
                    }}
                    placeholder="Seleccionar plan..."
                  />
                </div>
                {uniqueStudents.length > 0 && (
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500 mb-1 block">Filtrar por alumno</Label>
                    <SearchableSelect
                      options={[
                        { value: '', label: 'Todos los alumnos' },
                        ...uniqueStudents.map(s => ({ value: s.id.toString(), label: s.name })),
                      ]}
                      value={selectedStudentId || null}
                      onChange={v => setSelectedStudentId(v ? String(v) : '')}
                      placeholder="Todos los alumnos"
                    />
                  </div>
                )}
                {selectedPlan && (
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedPlan(null); setSelectedStudentId(''); }} className="text-gray-500">
                    <RotateCcw className="h-4 w-4 mr-1" /> Limpiar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {!selectedPlan ? (
            <Card>
              <CardContent className="flex flex-col items-center py-16 text-gray-400">
                <CreditCard className="h-16 w-16 mb-4 opacity-30" />
                <p className="text-sm">Selecciona un plan para ver las cuotas</p>
              </CardContent>
            </Card>
          ) : loadingInstallments ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>
          ) : (
            <>
              {selectedPlan && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Cuotas pendientes', value: filteredInstallments.filter(i => i.status === 'pending').length, color: 'bg-yellow-50 text-yellow-700' },
                    { label: 'Pagadas (pronto pago)', value: filteredInstallments.filter(i => i.status === 'paid_early').length, color: 'bg-green-50 text-green-700' },
                    { label: 'Pagadas (normal)', value: filteredInstallments.filter(i => i.status === 'paid_normal').length, color: 'bg-blue-50 text-blue-700' },
                    { label: 'Pagadas (con recargo)', value: filteredInstallments.filter(i => i.status === 'paid_late').length, color: 'bg-red-50 text-red-700' },
                  ].map(stat => (
                    <div key={stat.label} className={`${stat.color} rounded-lg p-3 text-center`}>
                      <p className="text-2xl font-bold">{stat.value}</p>
                      <p className="text-xs">{stat.label}</p>
                    </div>
                  ))}
                </div>
              )}

              <Card>
                <CardContent className="p-0">
                  <div className="overflow-auto max-h-[600px]">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">NUI</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Alumno</th>
                          <th className="px-4 py-3 text-center font-medium text-gray-600">#</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Vencimiento</th>
                          <th className="px-4 py-3 text-right font-medium text-green-700">Pronto pago <span className="text-xs font-normal text-gray-400">(c/IVA)</span></th>
                          <th className="px-4 py-3 text-right font-medium text-gray-700">Normal <span className="text-xs font-normal text-gray-400">(c/IVA)</span></th>
                          <th className="px-4 py-3 text-right font-medium text-red-700">Con recargo <span className="text-xs font-normal text-gray-400">(c/IVA)</span></th>
                          <th className="px-4 py-3 text-center font-medium text-gray-600">Estado</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">Cobrado</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Método de pago</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInstallments.map(inst => {
                          const sc = getScenario(inst.dueDate, inst.graceDeadline, inst.status);
                          const discAmt = inst.discountAmountWithTax ?? Number(inst.discountAmount);
                          const normAmt = inst.baseAmountWithTax ?? Number(inst.baseAmount);
                          const surAmt = inst.surchargeAmountWithTax ?? Number(inst.surchargeAmount);
                          return (
                            <tr key={inst.id} className={`border-t hover:bg-gray-50 ${inst.status !== 'pending' ? 'opacity-60' : ''}`}>
                              <td className="px-4 py-2 font-mono text-xs text-indigo-600 font-medium whitespace-nowrap">{inst.studentNui ?? '—'}</td>
                              <td className="px-4 py-2 font-medium truncate max-w-[160px]">{inst.studentName}</td>
                              <td className="px-4 py-2 text-center text-gray-400 font-mono">{inst.installmentNumber}</td>
                              <td className="px-4 py-2 text-gray-600">{fmtDate(inst.dueDate)}</td>
                              <td className="px-4 py-2 text-right text-green-700">{fmtCurrency(discAmt)}</td>
                              <td className="px-4 py-2 text-right">{fmtCurrency(normAmt)}</td>
                              <td className="px-4 py-2 text-right text-red-700">{fmtCurrency(surAmt)}</td>
                              <td className="px-4 py-2 text-center">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${SCENARIO_COLOR[sc]}`}>
                                  {SCENARIO_LABEL[sc]}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-right font-medium">
                                {inst.paidAmount ? fmtCurrency(inst.paidAmount) : '—'}
                              </td>
                              <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{fmtPaymentMethods(inst.paymentMethods)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div className="flex gap-2">
              <span className="text-green-600 font-bold text-lg leading-none">A</span>
              <div>
                <p className="font-semibold text-green-800">Pronto Pago</p>
                <p className="text-gray-600">Pago en o antes de la fecha de vencimiento → descuento del {form.discountPct}%</p>
              </div>
            </div>
            <div className="flex gap-2">
              <span className="text-yellow-600 font-bold text-lg leading-none">B</span>
              <div>
                <p className="font-semibold text-yellow-800">Periodo de Gracia</p>
                <p className="text-gray-600">Hasta {form.graceDays} días después → precio normal sin penalización</p>
              </div>
            </div>
            <div className="flex gap-2">
              <span className="text-red-600 font-bold text-lg leading-none">C</span>
              <div>
                <p className="font-semibold text-red-800">Recargo por Mora</p>
                <p className="text-gray-600">Día {form.graceDays + 1} en adelante → {form.surchargePct}% de recargo. Regla FIFO: se aplica a deuda más antigua.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
